import { concatFloat32 } from '../audio/wav';

export interface UtteranceCaptureOptions {
  sampleRate?: number;
  /** Non-energetic audio needed to declare an endpoint (after `armEndpointing`). */
  silenceMs: number;
  /** Silence is ignored for this long after `armEndpointing` (TTS stop / echo decay). */
  holdMs: number;
  /** Hard cap: force an endpoint even while the user is still talking. */
  maxMs: number;
  /** Buffered audio above this triggers `needsFlush()` (STT the oldest chunk, then drop it). */
  maxBufferedMs?: number;
  /** After a flush, keep at least this much of the newest audio buffered. */
  keepAfterFlushMs?: number;
  /** Consecutive energetic frames required before a frame counts as speech (noise debounce). */
  minEnergeticRun?: number;
}

export type CaptureTick = 'speech' | 'silence' | 'endpoint' | 'max';

/**
 * Continuous mic capture for one user utterance that started during assistant playback.
 *
 * Invariant: every sample is transcribed at most once. Audio is either buffered here (to be
 * transcribed at commit) or has already been flushed to `flushedText` and dropped. Callers must
 * never merge two transcripts of overlapping audio — that is how duplicated turns were produced.
 *
 * Endpointing uses caller-supplied energy/VAD evidence rather than Silero segment ends, because
 * Silero frequently reports speechEnd the moment TTS stops even though the user is mid-sentence.
 */
export class UtteranceCapture {
  readonly startTs: number;
  private readonly sr: number;
  private readonly opts: Required<UtteranceCaptureOptions>;
  private readonly frames: Float32Array[] = [];
  private readonly frameRms: number[] = [];
  /** Wall time each frame was pushed (lead-in uses startTs). */
  private readonly frameTs: number[] = [];
  private buffered = 0;
  private dropped = 0;
  private silence = 0;
  private energeticRun = 0;
  private lastEnergeticTotal = 0;
  private lastEnergeticTsValue: number;
  private endpointArmedAt: number | null = null;
  private readonly flushed: string[] = [];

  constructor(startTs: number, lead: Float32Array | null, opts: UtteranceCaptureOptions) {
    this.startTs = startTs;
    this.sr = opts.sampleRate ?? 16000;
    this.opts = {
      sampleRate: this.sr,
      silenceMs: opts.silenceMs,
      holdMs: opts.holdMs,
      maxMs: opts.maxMs,
      maxBufferedMs: opts.maxBufferedMs ?? 25_000,
      keepAfterFlushMs: opts.keepAfterFlushMs ?? 12_000,
      minEnergeticRun: opts.minEnergeticRun ?? 2,
    };
    this.lastEnergeticTsValue = startTs;
    if (lead?.length) {
      this.frames.push(lead);
      this.frameRms.push(0);
      this.frameTs.push(startTs - (lead.length / this.sr) * 1000);
      this.buffered += lead.length;
    }
  }

  /**
   * Drop buffered audio recorded before `ts - padMs` (e.g. assistant bleed before the clean
   * cut). Keeps `totalSamples` monotonic so `grewSince` marks stay valid.
   */
  dropBefore(ts: number, padMs = 450): number {
    const cutoff = ts - padMs;
    let n = 0;
    while (this.frames.length > 1 && this.frameTs[0] < cutoff) {
      const f = this.frames.shift()!;
      this.frameRms.shift();
      this.frameTs.shift();
      n += f.length;
    }
    this.buffered -= n;
    this.dropped += n;
    return n;
  }

  /** Start counting silence toward an endpoint (called when the assistant has been stopped). */
  armEndpointing(nowTs: number) {
    this.endpointArmedAt = nowTs;
    this.silence = 0;
  }

  get endpointingArmed(): boolean {
    return this.endpointArmedAt !== null;
  }

  /** Append one VAD frame. `energetic` is the caller's speech evidence for this frame. */
  push(frame: Float32Array, energetic: boolean, rms: number, nowTs: number): CaptureTick {
    this.frames.push(frame);
    this.frameRms.push(rms);
    this.frameTs.push(nowTs);
    this.buffered += frame.length;
    const ms = (frame.length / this.sr) * 1000;

    if (energetic) {
      this.energeticRun++;
      if (this.energeticRun >= this.opts.minEnergeticRun) {
        this.lastEnergeticTotal = this.totalSamples;
        this.lastEnergeticTsValue = nowTs;
        this.silence = 0;
        return 'speech';
      }
    } else {
      this.energeticRun = 0;
    }

    if (this.endpointArmedAt === null || nowTs - this.endpointArmedAt < this.opts.holdMs) return 'silence';
    this.silence += ms;
    if (nowTs - this.startTs >= this.opts.maxMs) return 'max';
    return this.silence >= this.opts.silenceMs ? 'endpoint' : 'silence';
  }

  /** Buffered (not yet flushed) samples. */
  get samples(): number {
    return this.buffered;
  }

  /** All samples ever pushed, including flushed ones — stable marks for `grewSince`. */
  get totalSamples(): number {
    return this.buffered + this.dropped;
  }

  get lastEnergeticTs(): number {
    return this.lastEnergeticTsValue;
  }

  get silenceMs(): number {
    return this.silence;
  }

  /** True when confirmed speech arrived after `totalSamples` was `mark`. */
  grewSince(mark: number): boolean {
    return this.lastEnergeticTotal > mark;
  }

  durationMs(): number {
    return (this.buffered / this.sr) * 1000;
  }

  /** Buffered audio (does not include flushed chunks). */
  audio(): Float32Array {
    return concatFloat32(this.frames);
  }

  needsFlush(): boolean {
    return this.durationMs() > this.opts.maxBufferedMs;
  }

  /**
   * Remove and return the oldest audio so the buffer shrinks to about `keepAfterFlushMs`.
   * Splits at the quietest frame in a 1.5 s window so words are not cut. Never overlaps with
   * what remains buffered.
   */
  takeFlushChunk(): Float32Array | null {
    const keep = Math.round((this.opts.keepAfterFlushMs / 1000) * this.sr);
    const target = this.buffered - keep;
    if (target <= 0) return null;
    let acc = 0;
    let i = 0;
    while (i < this.frames.length && acc < target) {
      acc += this.frames[i].length;
      i++;
    }
    // Search a 1.5 s window ending at i for the quietest frame to split on.
    const windowFrames = Math.ceil((1500 / 1000) * this.sr / 512);
    let best = i;
    let bestRms = Number.POSITIVE_INFINITY;
    for (let j = Math.max(1, i - windowFrames); j <= i && j < this.frames.length; j++) {
      const r = this.frameRms[j];
      if (r < bestRms) {
        bestRms = r;
        best = j;
      }
    }
    if (best <= 0) return null;
    const chunkFrames = this.frames.splice(0, best);
    this.frameRms.splice(0, best);
    this.frameTs.splice(0, best);
    const chunk = concatFloat32(chunkFrames);
    this.buffered -= chunk.length;
    this.dropped += chunk.length;
    return chunk;
  }

  addFlushedText(text: string) {
    const t = text.trim();
    if (t) this.flushed.push(t);
  }

  get flushedText(): string {
    return this.flushed.join(' ');
  }

  /** Flushed text plus the transcript of the currently buffered audio. */
  fullText(bufferedTranscript: string): string {
    return [this.flushedText, bufferedTranscript.trim()].filter(Boolean).join(' ');
  }
}
